import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { usePermissions } from "../../PermissionsProvider";
import { ConfigService } from "../../services/ConfigService";
import { ModelConfig } from "../../model_gen/config";
import { Agent } from "../../model_gen/agent_base";

interface AgentSettings {
  // AgentRequest fields
  max_iterations?: number | null;
  execution_mode?: string | null;
  high_performance?: boolean | null;
  disable_chaining?: boolean | null;

  // ChatCompletionParams fields
  model?: string | null;

  // Version information
  version?: {
    agent_version_number?: string | null;
    agent_release_name?: string | null;
  } | null;
}

interface ModelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  companyId?: number;
  showEmptyOption?: boolean;
  intentGroupId?: number;
  modelType?: "all" | "server" | "client";
  agentSettings?: AgentSettings | null;
  onAgentParamsChange?: (params: {
    agent_id: number | null;
    settings: AgentSettings | null;
  }) => void;
}

const ModelInput: React.FC<ModelInputProps> = ({
  value,
  onChange,
  placeholder = "Select a model",
  label = "Model",
  disabled = false,
  style = {},
  companyId = -1,
  showEmptyOption = true,
  intentGroupId,
  modelType = "all",
  agentSettings: externalAgentSettings,
  onAgentParamsChange,
}) => {
  const { apiKey } = usePermissions();
  const [models, setModels] = useState<string[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Use external agentSettings as source of truth, with fallback to empty object
  const agentSettings = useMemo(() => {
    return externalAgentSettings || {};
  }, [externalAgentSettings]);

  useEffect(() => {
    setIsLoading(true);
    const modelsSubscription = ConfigService.getModels(
      intentGroupId ?? companyId,
      null,
      apiKey
    ).subscribe(
      (modelData: ModelConfig) => {
        let modelsToDisplay: string[] = [];

        if (modelType === "server" && modelData.server_models) {
          modelsToDisplay = modelData.server_models;
        } else if (modelType === "client" && modelData.client_models) {
          modelsToDisplay = modelData.client_models;
        } else {
          modelsToDisplay = modelData.all_models || [];
        }

        setModels(modelsToDisplay);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setIsLoading(false);
      }
    );

    return () => {
      modelsSubscription.unsubscribe();
    };
  }, [apiKey, companyId, intentGroupId, modelType]);

  // Fetch agents only once per apiKey
  const fetchedAgentsApiKeyRef = useRef<any>(null);
  useEffect(() => {
    if (!apiKey) return;
    if (fetchedAgentsApiKeyRef.current === apiKey && agents.length > 0) return;
    fetchedAgentsApiKeyRef.current = apiKey;

    const agentsSubscription = ConfigService.getAgents(null, apiKey).subscribe(
      (response: any) => {
        const fetchedAgents = Array.isArray(response)
          ? response
          : response.agents || [];
        setAgents(fetchedAgents);
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
      }
    );

    return () => {
      agentsSubscription.unsubscribe();
    };
  }, [apiKey]);

  // Helper functions
  const groupModelsByProvider = (models: string[]) => {
    const grouped: { [provider: string]: string[] } = {};
    models.forEach((model) => {
      const [provider] = model.split(":");
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      if (!grouped[provider].includes(model)) {
        grouped[provider].push(model);
      }
    });
    return grouped;
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to check if selected value is an agent
  const isAgentSelected = (value?: string) => {
    return value?.startsWith("agent:");
  };

  // Helper function to parse agent model string
  // Format: agent:{id} or agent:{id}:{version} or agent:{id}:release/{release_name}
  const parseAgentModelString = (value?: string): { agentId: string | null; version: string | null; isRelease: boolean } => {
    if (!isAgentSelected(value)) return { agentId: null, version: null, isRelease: false };
    
    const parts = value?.split(":") || [];
    if (parts.length < 2) return { agentId: null, version: null, isRelease: false };
    
    const agentId = parts[1];
    if (parts.length === 2) {
      return { agentId, version: null, isRelease: false };
    }
    
    const versionPart = parts.slice(2).join(":"); // Handle colons in version numbers
    if (versionPart.startsWith("release/")) {
      return { agentId, version: versionPart.replace("release/", ""), isRelease: true };
    }
    
    return { agentId, version: versionPart, isRelease: false };
  };

  // Helper function to get selected agent
  const getSelectedAgent = (value?: string) => {
    const { agentId } = parseAgentModelString(value);
    if (!agentId) return null;
    return agents.find((agent) => agent.id?.toString() === agentId) || null;
  };

  // Helper function to build agent model string
  const buildAgentModelString = (agentId: number, version: string | null, isRelease: boolean): string => {
    if (!version) {
      return `agent:${agentId}`;
    }
    if (isRelease) {
      return `agent:${agentId}:release/${version}`;
    }
    return `agent:${agentId}:${version}`;
  };

  // Store releases per agent ID (fetched on mount)
  const [agentReleasesMap, setAgentReleasesMap] = useState<Map<number, Array<{ name: string; version_number: string; is_default?: boolean }>>>(new Map());
  
  // Fetch releases for all agents once per apiKey
  useEffect(() => {
    if (!apiKey) return;

    const releasesSubscription = ConfigService.getAgentsWithReleases(null, apiKey).subscribe(
      (response: any) => {
        const releases = response.agent_releases || [];
        
        const newMap = new Map<number, Array<{ name: string; version_number: string; is_default?: boolean }>>();
        
        releases.forEach((r: any) => {
          const agentId = r.agent_id;
          if (!newMap.has(agentId)) {
            newMap.set(agentId, []);
          }
          newMap.get(agentId)?.push({
            name: r.release_name,
            version_number: r.release_version,
            is_default: r.release_is_default
          });
        });
        
        setAgentReleasesMap(newMap);
      },
      (error: any) => {
        console.error("Error fetching agent releases:", error);
      }
    );

    return () => {
      releasesSubscription.unsubscribe();
    };
  }, [apiKey]);

  const handleValueChange = (newValue: string) => {
    // If selecting just an agent (without version/release), use draft (backend will default to latest if no draft)
    if (isAgentSelected(newValue) && newValue.split(":").length === 2) {
      const agent = getSelectedAgent(newValue);
      if (agent?.id) {
        const agentId = Number(agent.id);
        const finalValue = `agent:${agentId}`;
        
        if (onChange) {
          onChange(finalValue);
        }
        if (onAgentParamsChange) {
          // No version info - backend will use draft or latest
          const defaults: AgentSettings = {
            max_iterations: agent?.max_iterations || null,
            execution_mode: "route",
            high_performance: true,
            disable_chaining: true,
            model: "openai:gpt-4o-mini",
            version: null, // No version specified - backend defaults to draft or latest
          };
          onAgentParamsChange({
            agent_id: agentId,
            settings: defaults,
          });
        }
        return;
      }
    }
    
    // Handle agent with version/release or regular model
    if (onChange) {
      onChange(newValue);
    }
    if (onAgentParamsChange) {
      if (isAgentSelected(newValue)) {
        const agent = getSelectedAgent(newValue);
        const { version, isRelease } = parseAgentModelString(newValue);
        
        // Build version info if version is present in model string
        let versionInfo: { agent_version_number: string | null; agent_release_name: string | null } | null = null;
        if (version) {
          if (isRelease) {
            versionInfo = { agent_version_number: null, agent_release_name: version };
          } else {
            versionInfo = { agent_version_number: version, agent_release_name: null };
          }
        }
        
        const defaults: AgentSettings = {
          max_iterations: agent?.max_iterations || null,
          execution_mode: "route",
          high_performance: true,
          disable_chaining: true,
          model: "openai:gpt-4o-mini",
          version: versionInfo,
        };
        onAgentParamsChange({
          agent_id: agent?.id ? Number(agent.id) : null,
          settings: defaults,
        });
      } else {
        onAgentParamsChange({ agent_id: null, settings: null });
      }
    }
  };

  // Helper function to get display value for Select component
  const getSelectValue = (value?: string): string | undefined => {
    return value || undefined;
  };

  // Helper function to get agent display name with releases
  const getAgentDisplayOptions = (agent: Agent) => {
    const agentId = agent.id ? Number(agent.id) : null;
    if (!agentId) return [];
    
    const releases = agentReleasesMap.get(agentId) || [];
    
    const options: Array<{ value: string; label: string }> = [];
    
    // Add main agent option (defaults to draft or latest)
    options.push({
      value: `agent:${agentId}`,
      label: agent.agent_name || `Agent ${agentId}`
    });
    
    // Add release options
    releases.forEach((release) => {
      options.push({
        value: `agent:${agentId}:release/${release.name}`,
        label: `${release.name} (${agent.agent_name})`
      });
    });
    
    return options;
  };

  // Group and sort models - memoized to avoid recalculations on every render
  const { groupedModels, sortedProviders } = useMemo(() => {
    const grouped = groupModelsByProvider(models);
    const sorted = Object.keys(grouped).sort((a, b) => {
      if (a.toLowerCase() === "maitai") return -1;
      if (b.toLowerCase() === "maitai") return 1;
      return a.localeCompare(b);
    });
    return { groupedModels: grouped, sortedProviders: sorted };
  }, [models]);

  return (
    <div style={style} className="space-y-2">
      <Select
        value={getSelectValue(value) || undefined}
        onValueChange={handleValueChange}
        disabled={disabled || isLoading}>
        <SelectTrigger className="w-full focus:ring-maitai-green focus:border-maitai-green">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-w-[400px]">
          {agents.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-maitai-green font-gc-med">
                Agent
              </SelectLabel>
              {agents.map((agent) => {
                const options = getAgentDisplayOptions(agent);
                return options.map((option) => (
                <SelectItem
                    key={option.value}
                    value={option.value}>
                    {option.label}
                </SelectItem>
                ));
              })}
            </SelectGroup>
          )}

          {sortedProviders.map((provider) => (
            <SelectGroup key={provider}>
              <SelectLabel
                className={
                  provider.toLowerCase() === "maitai"
                    ? "text-maitai-green font-gc-med"
                    : "font-gc-med"
                }>
                {provider.toLowerCase() === "maitai"
                  ? `${capitalizeFirstLetter(provider)} (Recommended)`
                  : capitalizeFirstLetter(provider)}
              </SelectLabel>
              {groupedModels[provider].map((model) => {
                const [, ...modelParts] = model.split(":");
                const modelName = modelParts.join(":");
                return (
                  <SelectItem key={model} value={model}>
                    {modelName}
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ModelInput;
